/**
 * OrejaClarificationPhase - Displays AI-generated clarification questions and
 * collects answers via voice or text input.
 *
 * Shows a collapsible transcript section and numbered question cards with
 * optional multiple-choice options. Used for both the 'clarifying' and
 * 'answering' phases.
 *
 * @module OrejaClarificationPhase
 */

import { useState, useCallback } from 'react';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Button } from '@/components/ui/button';
import { ChevronDown, HelpCircle, Loader2, SkipForward } from 'lucide-react';
import { cn } from '@/lib/utils';
import { OrejaVoiceTextInput, type OrejaInputPayload } from './OrejaVoiceTextInput';
import type { OrejaClarificationQuestion } from '@/lib/oreja/types';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Strip bracketed ClickUp task IDs from display text (e.g. "[86b7x19mg] Task Name" → "Task Name"). */
function stripTaskId(text: string): string {
  return text.replace(/^\[[\da-z]+\]\s*/i, '');
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface OrejaClarificationPhaseProps {
  /** The transcript from the initial recording. */
  transcript: string;
  /** AI-generated clarification questions. */
  questions: OrejaClarificationQuestion[];
  /** Callback when the user submits their answers (with optional radio context). */
  onSendAnswers: (input: { type: 'voice'; blob: Blob } | { type: 'text'; text: string }, radioContext?: string) => void;
  /** Callback when the user skips clarification and goes straight to proposals. */
  onSkipClarification?: (radioContext?: string) => void;
  /** Whether the parent is processing. */
  isLoading?: boolean;
}

// ---------------------------------------------------------------------------
// Sub-component: Question card
// ---------------------------------------------------------------------------

interface QuestionCardProps {
  question: OrejaClarificationQuestion;
  index: number;
  selectedOption: string;
  onOptionChange: (questionId: string, value: string) => void;
}

/**
 * Single clarification question card with optional radio options.
 *
 * @param props - Question card props
 * @returns A numbered question card element
 */
function QuestionCard({ question, index, selectedOption, onOptionChange }: QuestionCardProps) {
  return (
    <div className="rounded-md border p-3 space-y-2">
      <div className="flex gap-2">
        <span className="text-sm font-bold text-muted-foreground shrink-0">
          {index + 1}.
        </span>
        <div className="space-y-1 min-w-0">
          <p className="text-sm font-medium">{stripTaskId(question.question)}</p>
          {question.context && (
            <p className="text-xs text-muted-foreground">{stripTaskId(question.context)}</p>
          )}
        </div>
      </div>

      {question.options && question.options.length > 0 && (
        <RadioGroup
          value={selectedOption}
          onValueChange={(val) => onOptionChange(question.id, val)}
          className="ml-5 space-y-1"
        >
          {question.options.map((opt, optIdx) => (
            <div key={optIdx} className="flex items-center gap-2">
              <RadioGroupItem
                value={opt}
                id={`${question.id}-opt-${optIdx}`}
              />
              <Label
                htmlFor={`${question.id}-opt-${optIdx}`}
                className="text-sm font-normal cursor-pointer"
              >
                {stripTaskId(opt)}
              </Label>
            </div>
          ))}
        </RadioGroup>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

/**
 * Clarification phase: shows transcript, questions, and answer input.
 *
 * @param props - Component props
 * @returns The clarification phase UI
 */
export function OrejaClarificationPhase({
  transcript,
  questions,
  onSendAnswers,
  onSkipClarification,
  isLoading = false,
}: OrejaClarificationPhaseProps) {
  const [transcriptOpen, setTranscriptOpen] = useState(true);
  const [selectedOptions, setSelectedOptions] = useState<Record<string, string>>({});

  const handleOptionChange = useCallback((questionId: string, value: string) => {
    setSelectedOptions((prev) => ({ ...prev, [questionId]: value }));
  }, []);

  /** Build a prefix string from any selected radio options. */
  const buildRadioContext = useCallback(() => {
    const entries = Object.entries(selectedOptions).filter(([, v]) => v);
    if (entries.length === 0) return '';
    const lines = entries.map(([qId, val]) => {
      const q = questions.find((q) => q.id === qId);
      return q ? `${q.question} → ${val}` : `${qId}: ${val}`;
    });
    return `Selected answers:\n${lines.join('\n')}`;
  }, [selectedOptions, questions]);

  // Voice auto-sends with radio context; text uses onTextSubmit
  const handleInput = useCallback((payload: OrejaInputPayload) => {
    if (payload.type === 'voice') {
      onSendAnswers(payload, buildRadioContext());
    }
  }, [onSendAnswers, buildRadioContext]);

  return (
    <div className="flex flex-col gap-4 p-4">
      {/* Loading overlay - show at top so it's always visible */}
      {isLoading && (
        <div className="flex items-center gap-2 py-3 px-4 text-sm font-medium bg-primary/10 border border-primary/20 rounded-lg">
          <Loader2 className="h-4 w-4 animate-spin text-primary" />
          Processing your answers...
        </div>
      )}

      {/* Phase header - make it very clear this is a clarification step */}
      {!isLoading && (
        <div className="rounded-lg bg-primary/10 border border-primary/20 p-3">
          <div className="flex items-center gap-2 mb-1">
            <HelpCircle className="h-4 w-4 text-primary" />
            <h3 className="text-sm font-semibold text-primary">Oreja needs your input</h3>
          </div>
          <p className="text-xs text-muted-foreground">
            I transcribed your update and have a few questions before generating task proposals.
            Record a voice answer or type below.
          </p>
        </div>
      )}

      {/* Transcript section (collapsible) */}
      <Collapsible open={transcriptOpen} onOpenChange={setTranscriptOpen}>
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="flex items-center gap-2 text-sm font-medium hover:text-foreground transition-colors w-full text-left"
          >
            <ChevronDown
              className={cn(
                'h-4 w-4 transition-transform',
                transcriptOpen && 'rotate-180',
              )}
            />
            Your transcript
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="mt-2 rounded-md bg-muted/50 p-3 text-sm text-muted-foreground whitespace-pre-wrap max-h-40 overflow-y-auto">
            {transcript}
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Questions section */}
      <div className="space-y-3">
        <h3 className="text-sm font-medium">Questions ({questions.length})</h3>
        {questions.map((q, idx) => (
          <QuestionCard
            key={q.id}
            question={q}
            index={idx}
            selectedOption={selectedOptions[q.id] ?? ''}
            onOptionChange={handleOptionChange}
          />
        ))}
      </div>

      {/* Skip clarification button */}
      {onSkipClarification && (
        <Button
          variant="outline"
          size="sm"
          className="min-h-[44px] w-full"
          disabled={isLoading}
          onClick={() => onSkipClarification(buildRadioContext())}
        >
          <SkipForward className="h-4 w-4 mr-2" />
          Looks good, skip to proposals
        </Button>
      )}

      {/* Answer input */}
      <div className="space-y-2">
        <div className="rounded-md bg-muted/50 p-2.5 space-y-1">
          <p className="text-xs font-medium">How to answer</p>
          <p className="text-xs text-muted-foreground">
            Select any radio buttons above that apply, then use voice or text below to
            add context or answer open-ended questions. Your radio selections and typed/spoken
            answer will be sent together.
          </p>
        </div>
        <OrejaVoiceTextInput
          onInput={handleInput}
          onTextSubmit={(text) => {
            onSendAnswers({ type: 'text', text }, buildRadioContext());
          }}
          placeholder="Type your answers or add context to your selections above..."
          disabled={isLoading}
        />
      </div>

    </div>
  );
}
